import { useState, useRef } from "react";
import { Upload, Link, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { searchUnsplashPhotos, UnsplashPhoto } from "@/services/unsplash.service";

// Link di attribution richiesto dalle Unsplash API Guidelines: utm_source/utm_medium
// vanno aggiunti a ogni link verso un profilo fotografo o verso unsplash.com.
function withUnsplashUtm(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}utm_source=Place2Be&utm_medium=referral`;
}

interface ImageUploadProps {
  image: string | null;
  onImageChange: (image: string | null) => void;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  disabled?: boolean;
  /** Se fornita, il file viene caricato su storage e onImageChange riceve il public URL */
  uploadFn?: (file: File) => Promise<string>;
  /** Override the height of the upload/preview container (default: "h-[350px]") */
  heightClass?: string;
  /** Mostra il messaggio di upload (default: true) */
  showUploadMessage?: boolean;
  /** Controlla la visibilità dell'input URL (controlled component) */
  showUrlInput?: boolean;
  /** Callback per cambiare la visibilità dell'input URL */
  onShowUrlInputChange?: (show: boolean) => void;
  /** Mostra il pulsante di ricerca Unsplash (default: true). Disattivarlo per loghi/immagini non stock */
  showUnsplashButton?: boolean;
}

const ImageUpload = ({ 
  image, 
  onImageChange, 
  maxSizeMB = 10,
  acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
  disabled = false,
  uploadFn,
  heightClass = 'h-[350px]',
  showUploadMessage = true,
  showUrlInput = false,
  onShowUrlInputChange,
  showUnsplashButton = true,
}: ImageUploadProps) => {
  const { t } = useI18n();
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUnsplash, setShowUnsplash] = useState(false);
  const [unsplashQuery, setUnsplashQuery] = useState("");
  const [unsplashResults, setUnsplashResults] = useState<UnsplashPhoto[]>([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashError, setUnsplashError] = useState<string | null>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (disabled || !files || files.length === 0) return;

    const file = files[0]; // Take only the first file

    // Validate file
    if (!acceptedFormats.includes(file.type)) {
      alert(`${t('imageUpload.invalidFormat')} ${acceptedFormats.join(', ')}`);
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`${t('imageUpload.fileTooLarge')} ${maxSizeMB}MB`);
      return;
    }

    if (uploadFn) {
      // Upload su storage e ritorna il public URL
      setIsLoading(true);
      uploadFn(file)
        .then((url) => onImageChange(url))
        .catch((err) => {
          console.error('Storage upload error:', err);
          alert(t('imageUpload.uploadFailed'));
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Fallback: converti in base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        console.log('Image loaded successfully:', result.substring(0, 50) + '...');
        onImageChange(result);
      }
    };
    reader.onerror = (e) => {
      console.error('Error reading file:', e);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim() || disabled) return;

    setIsLoading(true);
    try {
      // Validate URL format
      const url = new URL(imageUrl);
      
      // Check if it's a valid image URL
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.startsWith('image/')) {
        alert(t('imageUpload.notAnImage'));
        return;
      }

      // Set the image URL directly
      onImageChange(imageUrl);
      setImageUrl("");
      onShowUrlInputChange?.(false);
    } catch (error) {
      console.error('Error loading image from URL:', error);
      alert(t('imageUpload.invalidUrl'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const handleUrlCancel = () => {
    setImageUrl("");
    onShowUrlInputChange?.(false);
  };

  const handleUnsplashSearch = async () => {
    if (!unsplashQuery.trim() || disabled) return;

    setUnsplashLoading(true);
    setUnsplashError(null);
    try {
      const { results } = await searchUnsplashPhotos({ query: unsplashQuery, per_page: 12 });
      setUnsplashResults(results);
    } catch (error) {
      console.error('Error searching Unsplash photos:', error);
      setUnsplashError(t('modals.unsplashSearchError'));
    } finally {
      setUnsplashLoading(false);
    }
  };

  const handleUnsplashKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUnsplashSearch();
    }
  };

  const handleSelectUnsplashPhoto = (photo: UnsplashPhoto) => {
    if (disabled) return;
    onImageChange(photo.url);
    closeUnsplash();
  };

  const closeUnsplash = () => {
    setShowUnsplash(false);
    setUnsplashQuery("");
    setUnsplashResults([]);
    setUnsplashError(null);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area - Always visible with image overlay */}
      <div className={`relative ${heightClass}`}>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors h-full flex flex-col justify-center items-center ${
            disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
              : isDragOver
              ? 'border-blue-400 bg-blue-50 cursor-pointer'
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {showUploadMessage && !image && (
            <>
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-2">{t('imageUpload.dragAndDrop')}</p>
              <p className="text-sm text-gray-500 mb-4">
                {acceptedFormats.join(', ').toUpperCase()} {t('imageUpload.upTo')} {maxSizeMB}MB
              </p>
            </>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>

        {/* Image Preview - Absolute positioned over upload area */}
        {image && (
          <div 
            className={`absolute inset-0 flex items-center justify-center bg-white bg-opacity-95 rounded-lg group ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={handleClick}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${image})` }}
              />
              
              {/* Hover overlay with upload icon - Only show if not disabled */}
              {!disabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex flex-col items-center justify-center text-white">
                    <Upload className="h-8 w-8 mb-2" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add from URL / Search Unsplash buttons */}
      <div className="flex justify-center gap-2">
        {showUrlInput && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onShowUrlInputChange?.(true)}
            disabled={disabled}
            className="flex items-center gap-2 text-sm"
          >
            <Link className="h-4 w-4" />
            <span className="text-xs">{t('imageUpload.addFromUrl')}</span>
          </Button>
        )}
        {showUnsplashButton && !showUnsplash && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowUnsplash(true)}
            disabled={disabled}
            className="flex items-center gap-2 text-sm"
          >
            <Search className="h-4 w-4" />
            <span className="text-xs">{t('modals.searchUnsplash')}</span>
          </Button>
        )}
      </div>

      {/* URL Input Section */}
      {showUrlInput && (
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 text-gray-600" />
            <span className="font-medium text-sm text-gray-700">{t('imageUpload.addImageFromUrl')}</span>
          </div>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder={t('imageUpload.urlPlaceholder')}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={disabled || isLoading}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={disabled || isLoading || !imageUrl.trim()}
              size="sm"
            >
              {isLoading ? t('common.loading') : t('imageUpload.add')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleUrlCancel}
              disabled={disabled || isLoading}
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Unsplash Search Section */}
      {showUnsplashButton && showUnsplash && (
        <div className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-sm text-gray-700">{t('modals.searchUnsplash')}</span>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={closeUnsplash} disabled={disabled}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={t('modals.unsplashSearchPlaceholder')}
              value={unsplashQuery}
              onChange={(e) => setUnsplashQuery(e.target.value)}
              onKeyPress={handleUnsplashKeyPress}
              disabled={disabled || unsplashLoading}
              className="flex-1"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleUnsplashSearch}
              disabled={disabled || unsplashLoading || !unsplashQuery.trim()}
            >
              {unsplashLoading ? t('common.loading') : t('common.search')}
            </Button>
          </div>

          {unsplashError && (
            <p className="text-sm text-red-600">{unsplashError}</p>
          )}

          {!unsplashLoading && !unsplashError && unsplashResults.length === 0 && unsplashQuery && (
            <p className="text-sm text-gray-500">{t('modals.unsplashNoResults')}</p>
          )}

          {unsplashResults.length > 0 && (
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {unsplashResults.map((photo) => (
                <div
                  key={photo.id}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => handleSelectUnsplashPhoto(photo)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelectUnsplashPhoto(photo); }}
                  aria-disabled={disabled}
                  className={`relative group rounded-md overflow-hidden border border-gray-200 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'
                  }`}
                >
                  <img src={photo.thumbUrl} alt={photo.description ?? ''} className="w-full h-20 object-cover" />
                  <a
                    href={withUnsplashUtm(photo.author.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate hover:underline"
                    title={t('modals.unsplashPhotoBy', { name: photo.author.name })}
                  >
                    {photo.author.name}
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ImageUpload;
